// Post-login step: pick the legal entity + operation unit this session works in, and a
// department — optional for OU-wide users, mandatory (and limited to their own) for members of a
// fundraising department. Everything in the app is scoped to this; cause is a per-page filter.

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Building2, Loader2, LogOut } from "lucide-react";

import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  fetchContextOptions,
  fetchDepartments,
  validateContext,
  type ContextOptions,
  type DepartmentOptions,
} from "@/lib/donation";
import { APP_NAME } from "@/lib/nav";
import { useSessionScope } from "@/lib/session-scope";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Combobox } from "@/components/ui/combobox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";

export default function SelectScope() {
  const { token, displayName, username, signOut } = useAuth();
  const { scope, setScope } = useSessionScope();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";

  const [options, setOptions] = useState<ContextOptions | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [leId, setLeId] = useState<number | null>(scope?.legalEntity.id ?? null);
  const [ouId, setOuId] = useState<number | null>(scope?.operationUnit.id ?? null);
  const [deptId, setDeptId] = useState<number | null>(scope?.department?.id ?? null);
  const [depts, setDepts] = useState<DepartmentOptions | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) return;
    const controller = new AbortController();
    fetchContextOptions(token, controller.signal)
      .then((o) => {
        setOptions(o);
        // Single choice → preselect it.
        if (o.legalEntities.length === 1) setLeId((v) => v ?? Number(o.legalEntities[0].id));
      })
      .catch((e) => {
        if ((e as Error).name === "AbortError") return;
        setLoadError(
          e instanceof ApiError && e.status === 403
            ? "Your account does not have access to donations. Ask the accounts team to grant it."
            : (e as Error).message
        );
      });
    return () => controller.abort();
  }, [token]);

  // OUs for the chosen LE: the mapped ones; if this LE has no mappings maintained, all of mine.
  const ouOptions = useMemo(() => {
    if (!options || leId == null) return [];
    const all = options.operationUnits.map((o) => ({ ...o, id: Number(o.id) }));
    const mapped = all.filter((o) => o.legalEntityIds.map(Number).includes(leId));
    return mapped.length ? mapped : all;
  }, [options, leId]);

  // Keep the OU consistent with the LE — but only once the options have loaded, otherwise
  // the saved OU would be wiped while the list is still empty.
  useEffect(() => {
    if (!options) return;
    if (ouId != null && !ouOptions.some((o) => o.id === ouId)) setOuId(null);
    if (ouId == null && ouOptions.length === 1) setOuId(ouOptions[0].id);
  }, [options, ouOptions, ouId]);

  // Departments belong to an operation unit — reload whenever the OU changes.
  useEffect(() => {
    setDepts(null);
    if (!token || ouId == null) return;
    const controller = new AbortController();
    fetchDepartments(token, ouId, controller.signal)
      .then((d) => {
        setDepts(d);
        setDeptId((v) => {
          if (v != null && d.departments.some((x) => Number(x.id) === v)) return v;
          // Locked to exactly one department → it is the only choice.
          return d.locked && d.departments.length === 1 ? Number(d.departments[0].id) : null;
        });
      })
      .catch((e) => {
        if ((e as Error).name !== "AbortError") setDepts({ departments: [], locked: false });
      });
    return () => controller.abort();
  }, [token, ouId]);

  // id 0 = "All departments" (OU-wide users only; the Combobox has no clear action).
  const deptOptions = useMemo(() => {
    const list = (depts?.departments ?? []).map((d) => ({ id: Number(d.id), name: d.name }));
    return depts?.locked ? list : [{ id: 0, name: "All departments" }, ...list];
  }, [depts]);
  const deptMissing = !!depts?.locked && deptId == null;

  async function handleContinue() {
    if (!token || leId == null || ouId == null) return;
    setSaving(true);
    try {
      const confirmed = await validateContext(token, leId, ouId, deptId);
      setScope(confirmed);
      navigate(next, { replace: true });
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  function handleSignOut() {
    // signOut() also ends the org-wide ICM session and navigates away.
    signOut();
  }

  const leOptions = (options?.legalEntities ?? []).map((l) => ({ id: Number(l.id), name: l.name }));

  return (
    <div className="flex min-h-svh items-start justify-center bg-muted/40 px-4 pt-[12vh] pb-8">
      <div className="flex w-full max-w-md flex-col gap-6">
        <div className="flex flex-col items-center gap-2 text-center">
          <span className="flex size-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
            <Building2 className="size-6" />
          </span>
          <h1 className="text-xl font-bold">{APP_NAME}</h1>
          <p className="text-sm text-muted-foreground">
            Hare Krishna{displayName || username ? `, ${displayName || username}` : ""}. Choose where you are
            working for this session.
          </p>
        </div>

        <Card>
          <CardContent className="flex flex-col gap-4">
            {loadError ? (
              <p className="text-sm text-destructive">{loadError}</p>
            ) : !options ? (
              <div className="flex flex-col gap-4">
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-14 w-full" />
              </div>
            ) : (
              <>
                <div className="flex flex-col gap-1.5">
                  <Label>Legal entity</Label>
                  <Combobox
                    options={leOptions}
                    value={leId}
                    onValueChange={setLeId}
                    placeholder="Select legal entity…"
                    emptyText="No legal entities assigned to you."
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>Operation unit</Label>
                  <Combobox
                    options={ouOptions}
                    value={ouId}
                    onValueChange={setOuId}
                    placeholder={leId == null ? "Select a legal entity first" : "Select operation unit…"}
                    emptyText="No operation units available."
                    disabled={leId == null}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>
                    Department{" "}
                    {!depts?.locked && <span className="font-normal text-muted-foreground">(optional)</span>}
                  </Label>
                  <Combobox
                    options={deptOptions}
                    value={depts?.locked ? deptId : (deptId ?? 0)}
                    onValueChange={(id) => setDeptId(id ? id : null)}
                    placeholder={ouId == null ? "Select an operation unit first" : depts?.locked ? "Select your department…" : "All departments"}
                    emptyText="No fundraising departments in this operation unit."
                    disabled={ouId == null || depts == null}
                  />
                  {depts?.locked && (
                    <p className="text-xs text-muted-foreground">You work in the department(s) you are a member of.</p>
                  )}
                </div>
                <Button onClick={handleContinue} disabled={leId == null || ouId == null || deptMissing || saving}>
                  {saving && <Loader2 className="animate-spin" />} Continue
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        <Button variant="ghost" className="self-center text-muted-foreground" onClick={handleSignOut}>
          <LogOut /> Sign out
        </Button>
      </div>
    </div>
  );
}
